import { daysUntil, type Assessment } from '../../lib/api'
import { StageTrack, Status, type Stage } from '../kit'
import { QUESTIONNAIRE_STAGES, dayMonth, isLate, questionnaireStates, questionnaireStatus } from './journey'
import { statusWord } from '../../lib/status'
import '../../styles/home.css'

// A questionnaire tracked like a parcel: sent, answering, answered, scored.
//
// # WHERE EACH LINE COMES FROM
//
// The stage is the assessment's status and nothing else. Under each stage sits
// the date or the person the assessment read returns for it: when it was sent,
// who answered, when the answers came back. A stage the API has no date for
// has no line, rather than an estimate.
//
// How far the vendor has got is a count the list read does not carry. A caller
// that has read the assessment itself passes it, counted the way the review
// screen counts it (every question in the version, answered or marked not
// applicable), and the Answering stage then says "70 of 74 answered". Without
// it the stage says whether the vendor has started, and nothing more.

export type AnsweredCount = { done: number; total: number }

function answeringSub(a: Assessment, answered?: AnsweredCount): string | undefined {
  if (answered && answered.total > 0 && (a.status === 'ISSUED' || a.status === 'IN_PROGRESS' || a.status === 'RETURNED')) {
    return answered.done + ' of ' + answered.total + ' answered'
  }
  switch (a.status) {
    // The sub-line under a stage says only what the chip above does not: the
    // vendor's progress. Its state word is the chip's, from lib/status.ts.
    case 'RETURNED':
      return statusWord('questionnaire', 'RETURNED').word
    case 'EXPIRED':
    case 'CANCELLED':
      return statusWord('questionnaire', a.status).word
  }
  return a.respondentName ? 'By ' + a.respondentName : undefined
}

function scoredSub(a: Assessment): string | undefined {
  switch (a.status) {
    case 'SUBMITTED':
    case 'UNDER_REVIEW':
      return 'Waiting for you'
    case 'PROVISIONAL':
      return 'From a document'
    case 'ACCEPTED':
      return statusWord('questionnaire', 'ACCEPTED').word
  }
  return undefined
}

export function AssessmentTracker({ assessment: a, answered }: { assessment: Assessment; answered?: AnsweredCount }) {
  const states = questionnaireStates(a.status)
  const status = questionnaireStatus(a)
  const late = isLate(a)
  const d = daysUntil(a.dueOn)
  const stages: Stage[] = [
    { key: 'sent', label: 'Sent', state: states[0] ?? 'upcoming', sub: a.issuedAt ? dayMonth(a.issuedAt) : undefined },
    { key: 'answering', label: QUESTIONNAIRE_STAGES[1], state: states[1] ?? 'upcoming', sub: answeringSub(a, answered) },
    // A questionnaire returned for more answers keeps the date it first came
    // back, which under a stage the vendor has not reached again would read as
    // answered, so the date shows only once the stage is reached.
    {
      key: 'answered',
      label: QUESTIONNAIRE_STAGES[2],
      state: states[2] ?? 'upcoming',
      sub: a.submittedAt && states[2] !== 'upcoming' ? dayMonth(a.submittedAt) : undefined,
    },
    { key: 'scored', label: 'Scored', state: states[3] ?? 'upcoming', sub: scoredSub(a) },
  ]

  return (
    <section className="as-tracker" aria-label="Where the questionnaire is">
      <div className="as-tracker-head">
        <Status tone={status.tone}>{status.text}</Status>
        {a.dueOn && (
          <span className={'as-due' + (late ? ' as-late' : '')}>
            {late && d != null ? 'Overdue by ' + -d + (d === -1 ? ' day' : ' days') : 'Due ' + dayMonth(a.dueOn)}
          </span>
        )}
      </div>
      <StageTrack stages={stages} label="Questionnaire progress" />
    </section>
  )
}
